export interface ActionState<T> { status: 'idle' | 'pending' | 'success' | 'error'; result?: T; error?: unknown }

// The lock changes synchronously, before React paints disabled. Epochs discard
// completions from an unmounted/previous flow; terminal success is immutable.
export class AsyncAction<T> {
  private state: ActionState<T> = { status: 'idle' }
  private epoch = 0
  private terminal = false
  private listeners = new Set<() => void>()
  getSnapshot = () => this.state
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => this.listeners.delete(listener) }
  private publish(state: ActionState<T>) { this.state = state; this.listeners.forEach((listener) => listener()) }
  cancel = () => { this.epoch++; if (!this.terminal) this.publish({ status: 'idle' }) }
  run = async (operation: () => Promise<T>, terminal = false): Promise<T | undefined> => {
    if (this.terminal || this.state.status === 'pending') return undefined
    const epoch = ++this.epoch
    this.publish({ status: 'pending' })
    try {
      const result = await operation()
      if (epoch !== this.epoch || this.terminal) return undefined
      this.terminal = terminal
      this.publish({ status: 'success', result })
      return result
    } catch (error) {
      if (epoch === this.epoch && !this.terminal) this.publish({ status: 'error', error })
      return undefined
    }
  }
}
